import PropTypes from 'prop-types';
import React, { useState } from 'react';
import EditIcon from '../../shared/Icons/EditIcon';
import TrashIcon from '../../shared/Icons/TrashIcon';
// Borrows the shared menu's button classes without mounting the component.
import '../../shared/components/ActionsMenu.css';

const TeamFunctionManager = ({
  teamFunctions,
  team,
  csrfToken,
  onCreateTeamFunction,
  onUpdateTeamFunction,
  onDeleteTeamFunction
}) => {
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleAdd = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await onCreateTeamFunction(csrfToken, team, trimmed);
      setNewName('');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (fn) => {
    setEditingId(fn.id);
    setEditingName(fn.name);
  };

  const saveEdit = async (fn) => {
    const trimmed = editingName.trim();
    if (!trimmed || trimmed === fn.name) {
      setEditingId(null);
      return;
    }
    setSubmitting(true);
    try {
      await onUpdateTeamFunction(csrfToken, team, fn, trimmed);
      setEditingId(null);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = (fn) => {
    const ok = window.confirm(
      `Members assigned to "${fn.name}" will have their Function cleared. Continue?`
    );
    if (ok) {
      onDeleteTeamFunction(csrfToken, team, fn);
    }
  };

  return (
    <div className="team-member-card team-function-manager" data-testid="team-function-manager">
      <div className="team-member-card-header">
        <div className="team-member-card-header-left">
          <div className="team-member-card-title-row">
            <h2 className="team-member-card-title">Manage Functions</h2>
            <span className="team-member-count-badge">
              {teamFunctions.length} function{teamFunctions.length === 1 ? '' : 's'}
            </span>
          </div>
          <p className="team-member-card-subtitle">
            Define the functions people on your team perform.
          </p>
        </div>
      </div>

      <div className="team-function-manager-body px-4 pb-4">
        <ul className="list-unstyled m-0">
          {teamFunctions.map((fn) => (
            <li
              key={fn.id}
              className="d-flex align-items-center py-2"
              style={{ gap: '8px', borderBottom: '1px solid var(--sl-border-secondary, #e9eaeb)' }}
            >
              {editingId === fn.id ? (
                <>
                  <input
                    type="text"
                    autoFocus
                    className="team-form-input"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        saveEdit(fn);
                      } else if (e.key === 'Escape') {
                        setEditingId(null);
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="team-btn-primary"
                    disabled={submitting}
                    onClick={() => saveEdit(fn)}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="team-btn-secondary"
                    onClick={() => setEditingId(null)}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <span className="flex-grow-1">{fn.name}</span>
                  <button
                    type="button"
                    className="team-actions-btn team-actions-btn-square"
                    onClick={() => startEdit(fn)}
                    aria-label={`Edit ${fn.name}`}
                  >
                    <EditIcon width="16" height="16" />
                  </button>
                  <button
                    type="button"
                    className="team-actions-btn team-actions-btn-square team-actions-btn-danger"
                    onClick={() => confirmDelete(fn)}
                    aria-label={`Delete ${fn.name}`}
                  >
                    <TrashIcon width="16" height="16" />
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>

        <div className="d-flex mt-3" style={{ gap: '8px' }}>
          <input
            type="text"
            className="team-form-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Add a function (e.g. A&R)"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAdd();
              }
            }}
          />
          <button
            type="button"
            className="team-btn-primary"
            disabled={submitting || !newName.trim()}
            onClick={handleAdd}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
};

TeamFunctionManager.propTypes = {
  teamFunctions: PropTypes.array.isRequired,
  team: PropTypes.object.isRequired,
  csrfToken: PropTypes.string.isRequired,
  onCreateTeamFunction: PropTypes.func.isRequired,
  onUpdateTeamFunction: PropTypes.func.isRequired,
  onDeleteTeamFunction: PropTypes.func.isRequired
};

export default TeamFunctionManager;
